// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { fontConfig } from "@bg/XDL/PreStyle/typography";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { css } from "styled-components";

type Props = {
    alphabet: LetterDetails[];
    prefix?: string;
    expanded?: boolean;
    onExpand?: any;
    extendedStyles?: AlphabetNavigationStyles;
    onLetterClick?: (letter: string) => void;
    hashLink?: any;
};

export interface LetterDetails {
    letter: string;
    linkable: boolean;
}

export interface AlphabetNavigationStyles {
    container?: InjectableCss;
    navigationItem?: InjectableCss;
    allButton?: InjectableCss;
    navigationLink?: LinkProps;
    navigationLinkText?: TypographyPresentationProps;
    navigationTextEmpty?: TypographyPresentationProps;
}

export const alphabetNavigationStyles: AlphabetNavigationStyles = {
    container: {
        css: css`
            text-transform: uppercase;
            justify-items: center;
            display: flex;
            flex-wrap: wrap;
            margin-bottom: 40px;
            justify-content: center;
            .nav-expand-all {
                button {
                    background: ${getColor("primary")};
                }
            }
        `,
    },
    navigationItem: {
        css: css`
            margin: 0;
            box-sizing: border-box;
            padding: 8px 15px;
            max-width: 40px;
            margin-left: 8px;
            margin-top: 8px;
            border: 1px solid ${getColor("common.ui3")};
            border-radius: 4px;
        `,
    },
    navigationLink: {
        css: css`
            p {
                font-weight: ${fontConfig.weight.semiBold} !important;
                font-size: ${fontConfig.fontSize.xSmall}px;
                color: ${getColor("text.main")} !important;
            }
        `,
        typographyProps: {
            transform: "uppercase",
            variant: "headerTertiary",
            forwardAs: "span",
        },
    },
    navigationTextEmpty: {
        css: css`
            opacity: 0.35;
            margin-top: 2px;
        `,
        transform: "uppercase",
        variant: "headerTertiary",
        forwardAs: "span",
        color: "text.disabled",
        size: fontConfig.fontSize.xSmall,
    },
    allButton: {
        css: css`
            height: 40px;
            width: 53px;
            padding: 9px 12px;
            margin-top: 8px;
            margin-left: 8px;
            max-width: 50px;
            span {
                font-size: ${fontConfig.fontSize.xSmall}px;
            }
        `,
    },
};

const StyledSpan = getStyledWrapper("span");

const AlphabetNavigation: FC<Props> = (props: Props) => {
    const [styles] = useState(() => mergeToNew(alphabetNavigationStyles, props.extendedStyles));
    const [alphabet] = useState(() => props.alphabet);

    const handleClickOfLetter = (letter: string) => {
        props.onLetterClick?.(letter);
    };
    
    const createHandleClickOfLetter = (letter: string) => () => {
        return handleClickOfLetter(letter);
    };

    if (alphabet.length <= 0) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container} data-test-selector="alphabetNavigation" id="alphabetNavigation">
            <Button {...styles.allButton} onClick={props.onExpand} disabled={props.expanded}>
                {translate("All")}
            </Button>
            {alphabet.map(alphabetItem => (
                <StyledSpan
                    {...styles.navigationItem}
                    key={alphabetItem.letter}
                    data-test-selector="alphabetNavigationItemLetter"
                >
                    {alphabetItem.linkable ? (
                        <Link {...styles.navigationLink} onClick={createHandleClickOfLetter(alphabetItem.letter)}>
                            {alphabetItem.letter}
                        </Link>
                    ) : (
                        <Typography {...styles.navigationTextEmpty}>{alphabetItem.letter}</Typography>
                    )}
                </StyledSpan>
            ))}
        </StyledWrapper>
    );
};

export default AlphabetNavigation;
